import type { UscContext } from "../../shared-logic/types/UscCommonTypes";
import type { ExternalPriceKey } from "./externalPriceUtils";

export enum ExternalPriceValueType {
    InclVat = "RSP VAT",
    ExclVat = "RSP NO VAT",
    LabourTime = "LABOUR TIME",
}

export enum ExternalPriceFuelType {
    Petrol = "PETROL",
    Diesel = "DIESEL",
    Electric = "ELECTRIC",
    Hydrogen = "HYDROGEN",
    Hybrid = "HYBRID",
    Lpg = "LPG",
}

export enum ExternalPriceType {
    Car = "CAR",
    Equipment = "EQUIPMENT",
    Accessory = "ACCESSORY",
    Pack = "PACK",
    ExteriorColour = "EXTERIORCOLOUR",
    Upholstery = "UPHOLSTERY",
    Service = "SERVICE",
    CarDelivery = "HOME_DELIVERY",
}

export enum ExternalPriceVehicleCategory {
    New = "NEW",
    Used = "USED",
    Stock = "STOCK",
}

export const ExternalPriceVehicleCategoryMap: { [key in UscContext]: ExternalPriceVehicleCategory } = {
    used: ExternalPriceVehicleCategory.Used,
    stock: ExternalPriceVehicleCategory.Stock,
};

export enum ExternalPriceVehicleNetwork {
    ToyotaNetwork = "toyotaNetwork",
    LexusNetwork = "lexusNetwork",
}

export enum ExternalPriceCostType {
    Gross = "GROSS",
    Net = "NET",
    TaxFree = "TAXFREE",
}

// Response from prices calculation.
// See https://toyota-europe.atlassian.net/wiki/spaces/PANEDF/pages/31722544/TMEF+-+API+-+Output+-+PRICES+Formulae
export type ExternalPriceResponseType = {
    Prices: {
        Type: ExternalPriceType;
        ID: string;
        Values: { Type: ExternalPriceValueType; Amount: number; Currency: string }[];
    }[];
    ExtraCosts?: ExternalPriceExtraCostsType[];
    AdditionalInfo?: {
        Value: number;
        Key: string;
    }[];
};

// Type returned when using the POST endpoints.
export type ExternalPriceServerErrorType = { status: number; message: string };

export type ExternalPriceExtraCostsType = {
    Key: ExternalPriceKey;
    Values: {
        Type: ExternalPriceCostType;
        Amount: number;
        Currency: string;
        IncludedInPrice?: { ID: string; Type: string };
    }[];

    // TFR Used - These values are used for monthly rates mapping.
    EposSelectionIndicator?: string;
    EposCode?: string;
    EposVatCode?: string;
};

// Payload for prices calculation.
// See https://toyota-europe.atlassian.net/wiki/spaces/PANEDF/pages/31719874/TMEF+-+API+-+Input
export type ExternalPricePayloadType = {
    Date: string;
    Product: {
        Suffix: {
            SSN: string;
        };
        FuelType: ExternalPriceFuelType;
        VehicleType: "PC" | "CV";
        Hybrid: boolean;
        Plugin: boolean;
    };
    Prices: {
        ID: string;
        Type: ExternalPriceType;
        Values: { Type: ExternalPriceValueType; Amount: number; Currency?: string }[];
    }[];
    Vehicle: {
        Category: ExternalPriceVehicleCategory;
        Network: ExternalPriceVehicleNetwork;
        FirstRegistrationDate: string;
    };
    Dealer: {
        SubdivisionCode: string;
        BranchCode: string;
    };
    TechnicalSpecifications: {
        WLTP: { TestPhaseName: string; Category: string; Type: string; Value: string }[];
        A2P: { MasterID: string; Value: string }[];
    };
};
